import React, { useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Phone, Mail, Users, Video, MessageCircle, MapPin, StickyNote,
  Plus, X, Trash2, Clock, ChevronDown, ChevronUp, Calendar, Edit, AlertCircle, ListTodo
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const TIPOS_INTERACAO = [
  { id: 'ligacao', nome: 'Ligação', icon: Phone, cor: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'email', nome: 'E-mail', icon: Mail, cor: 'text-purple-600 bg-purple-50 border-purple-200' },
  { id: 'reuniao', nome: 'Reunião Presencial', icon: Users, cor: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { id: 'videoconferencia', nome: 'Videoconferência', icon: Video, cor: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  { id: 'whatsapp', nome: 'WhatsApp', icon: MessageCircle, cor: 'text-green-600 bg-green-50 border-green-200' },
  { id: 'visita_tecnica', nome: 'Visita Técnica', icon: MapPin, cor: 'text-orange-600 bg-orange-50 border-orange-200' },
  { id: 'nota', nome: 'Nota Interna', icon: StickyNote, cor: 'text-slate-600 bg-slate-50 border-slate-200' },
];

const getTipoConfig = (tipoId) => TIPOS_INTERACAO.find(t => t.id === tipoId) || TIPOS_INTERACAO[6];

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const formatDateShort = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '-'; }
};

export const InteracoesTimeline = ({ oportunidadeId, interacoes = [], onUpdate, onCriarTarefa }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [form, setForm] = useState({
    tipo: 'ligacao',
    descricao: '',
    data_interacao: new Date().toISOString().slice(0, 16),
    duracao_minutos: null,
    participantes: '',
    proxima_acao: '',
    data_proxima_acao: '',
  });

  const resetForm = () => {
    setForm({
      tipo: 'ligacao', descricao: '',
      data_interacao: new Date().toISOString().slice(0, 16),
      duracao_minutos: null, participantes: '', proxima_acao: '', data_proxima_acao: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (interacao) => {
    setForm({
      tipo: interacao.tipo || 'nota',
      descricao: interacao.descricao || '',
      data_interacao: interacao.data_interacao ? interacao.data_interacao.slice(0, 16) : '',
      duracao_minutos: interacao.duracao_minutos || null,
      participantes: interacao.participantes || '',
      proxima_acao: interacao.proxima_acao || '',
      data_proxima_acao: interacao.data_proxima_acao ? interacao.data_proxima_acao.slice(0, 10) : '',
    });
    setEditingId(interacao.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.descricao.trim()) { toast.error('Informe a descrição da interação'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        duracao_minutos: form.duracao_minutos ? parseInt(form.duracao_minutos) : null,
        data_proxima_acao: form.data_proxima_acao || null,
      };

      if (editingId) {
        await api.put(`/crm-oportunidades/${oportunidadeId}/interacoes/${editingId}`, payload);
        toast.success('Interação atualizada');
      } else {
        await api.post(`/crm-oportunidades/${oportunidadeId}/interacoes`, payload);
        toast.success('Interação registrada');
      }
      resetForm();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar interação');
    } finally { setSaving(false); }
  };

  const handleDelete = async (interacaoId) => {
    try {
      await api.delete(`/crm-oportunidades/${oportunidadeId}/interacoes/${interacaoId}`);
      toast.success('Interação removida');
      if (onUpdate) onUpdate();
    } catch { toast.error('Erro ao remover'); }
  };

  // Sort + filter
  const sorted = [...interacoes].sort((a, b) => (b.data_interacao || b.created_at || '').localeCompare(a.data_interacao || a.created_at || ''));
  const filtered = filtroTipo === 'todos' ? sorted : sorted.filter(i => i.tipo === filtroTipo);

  // Próximas ações pendentes
  const acoesPendentes = interacoes.filter(i => i.proxima_acao && i.data_proxima_acao && new Date(i.data_proxima_acao) >= new Date(new Date().toDateString()));

  return (
    <div data-testid="interacoes-timeline" className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Interações ({interacoes.length})</h4>
        <Button data-testid="add-interacao-btn" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => { resetForm(); setShowForm(true); }}>
          <Plus className="w-3 h-3" /> Registrar
        </Button>
      </div>

      {/* Ações pendentes */}
      {acoesPendentes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1">
          <div className="flex items-center gap-1 text-xs font-semibold text-amber-700">
            <AlertCircle className="w-3.5 h-3.5" /> Próximas Ações
          </div>
          {acoesPendentes.map(a => (
            <div key={a.id} className="text-xs text-amber-700 flex items-center gap-2 pl-4">
              <Calendar className="w-3 h-3" />
              <span className="font-medium">{formatDateShort(a.data_proxima_acao)}</span>
              <span className="flex-1">{a.proxima_acao}</span>
              {onCriarTarefa && (
                <button
                  onClick={() => onCriarTarefa(a.proxima_acao, a.data_proxima_acao)}
                  className="flex items-center gap-0.5 text-[10px] font-medium text-amber-800 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 rounded px-1.5 py-0.5 transition-colors"
                  title="Converter em tarefa"
                  data-testid="btn-converter-tarefa"
                >
                  <ListTodo className="w-3 h-3" /> Criar Tarefa
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form (inline) */}
      {showForm && (
        <div data-testid="interacao-form" className="border border-blue-200 rounded-lg p-3 bg-blue-50/30 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">{editingId ? 'Editar Interação' : 'Nova Interação'}</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Tipo *</Label>
              <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="select-tipo-interacao" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS_INTERACAO.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2"><t.icon className="w-3.5 h-3.5" />{t.nome}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Data/Hora</Label>
              <Input type="datetime-local" value={form.data_interacao} onChange={e => setForm(f => ({ ...f, data_interacao: e.target.value }))} className="h-8 text-xs" />
            </div>
          </div>

          <div>
            <Label className="text-[10px]">Descrição *</Label>
            <Textarea
              data-testid="input-descricao-interacao"
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              rows={3} className="text-xs" placeholder="Descreva o que foi tratado..."
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Duração (minutos)</Label>
              <Input type="number" min="0" value={form.duracao_minutos || ''} onChange={e => setForm(f => ({ ...f, duracao_minutos: e.target.value }))} className="h-8 text-xs" placeholder="Ex: 30" />
            </div>
            <div>
              <Label className="text-[10px]">Participantes</Label>
              <Input value={form.participantes} onChange={e => setForm(f => ({ ...f, participantes: e.target.value }))} className="h-8 text-xs" placeholder="Ex: João, Maria" />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-2 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Próxima Ação</Label>
              <Input value={form.proxima_acao} onChange={e => setForm(f => ({ ...f, proxima_acao: e.target.value }))} className="h-8 text-xs" placeholder="Ex: Enviar proposta" />
            </div>
            <div>
              <Label className="text-[10px]">Data Próxima Ação</Label>
              <Input type="date" value={form.data_proxima_acao} onChange={e => setForm(f => ({ ...f, data_proxima_acao: e.target.value }))} className="h-8 text-xs" />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={resetForm}>Cancelar</Button>
            <Button data-testid="save-interacao-btn" size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving}>
              {editingId ? 'Atualizar' : 'Registrar'}
            </Button>
          </div>
        </div>
      )}

      {/* Filter */}
      {interacoes.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          <Button variant={filtroTipo === 'todos' ? 'default' : 'ghost'} size="sm" className="h-6 text-[10px] px-2" onClick={() => setFiltroTipo('todos')}>
            Todos ({interacoes.length})
          </Button>
          {TIPOS_INTERACAO.map(t => {
            const count = interacoes.filter(i => i.tipo === t.id).length;
            if (count === 0) return null;
            return (
              <Button key={t.id} variant={filtroTipo === t.id ? 'default' : 'ghost'} size="sm" className="h-6 text-[10px] px-2 gap-1" onClick={() => setFiltroTipo(t.id)}>
                <t.icon className="w-3 h-3" /> {count}
              </Button>
            );
          })}
        </div>
      )}

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="text-center py-6 text-slate-400">
          <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-xs">Nenhuma interação registrada</p>
          <p className="text-[10px] mt-1">Registre ligações, emails, reuniões e mais</p>
        </div>
      ) : (
        <div className="space-y-0 relative">
          {/* Timeline line */}
          <div className="absolute left-[15px] top-3 bottom-3 w-px bg-slate-200" />

          {filtered.map((interacao, idx) => {
            const tipoCfg = getTipoConfig(interacao.tipo);
            const Icon = tipoCfg.icon;

            return (
              <div key={interacao.id || idx} data-testid={`interacao-item-${idx}`} className="relative pl-10 pb-3 group">
                {/* Icon dot */}
                <div className={`absolute left-1.5 top-1 w-7 h-7 rounded-full border flex items-center justify-center ${tipoCfg.cor}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>

                <div className="bg-white border border-slate-100 rounded-lg p-2.5 hover:border-slate-200 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] py-0 ${tipoCfg.cor}`}>{tipoCfg.nome}</Badge>
                        <span className="text-[10px] text-slate-400">{formatDate(interacao.data_interacao || interacao.created_at)}</span>
                        {interacao.duracao_minutos && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{interacao.duracao_minutos}min</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{interacao.descricao}</p>
                      {interacao.participantes && (
                        <p className="text-[10px] text-slate-400 mt-1"><Users className="w-3 h-3 inline mr-1" />{interacao.participantes}</p>
                      )}
                      {interacao.proxima_acao && (
                        <div className="mt-1.5 bg-amber-50 border border-amber-100 rounded px-2 py-1 text-[10px] text-amber-700 flex items-center gap-1.5 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">Próxima ação:</span> {interacao.proxima_acao}
                            {interacao.data_proxima_acao && <span className="ml-1 text-amber-500">({formatDateShort(interacao.data_proxima_acao)})</span>}
                          </div>
                          {onCriarTarefa && (
                            <button
                              onClick={() => onCriarTarefa(interacao.proxima_acao, interacao.data_proxima_acao)}
                              className="flex items-center gap-0.5 text-[10px] font-medium text-amber-800 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 rounded px-1.5 py-0.5 transition-colors flex-shrink-0"
                              title="Converter em tarefa"
                            >
                              <ListTodo className="w-3 h-3" /> Tarefa
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(interacao)} title="Editar">
                        <Edit className="w-3 h-3 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(interacao.id)} title="Excluir">
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">por {interacao.usuario_nome}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
